import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { api, TICKET_BEREICH_GRUPPEN, type Ticket } from '../lib/api';
import { useTicketNeu } from '../components/TicketNeu';
import { istAbgeschlossen, statusInfo, relativZeit, fmtDatumZeit, type TicketStatus } from '../lib/ticketStatus';
import { Zeitleiste } from './MeineTickets';

// Ticket-Master — Bearbeiter-Ansicht: Kennzahlen oben, Suche + Filter, kompakte Liste,
// Detail-Panel rechts (Beschreibung, Zeitleiste, Antworten, Anhänge, alle Aktionen), Mehrfachauswahl.
// Wer nur meldet, wird auf „Meine Tickets“ (/meine-tickets) geleitet.

const PRIO: Record<string, { label: string; cls: string; rang: number }> = {
  dringend: { label: 'Dringend', cls: 'text-red-700 bg-red-50 ring-1 ring-red-200', rang: 0 },
  wichtig:  { label: 'Wichtig',  cls: 'text-amber-800 bg-amber-50 ring-1 ring-amber-200', rang: 1 },
  normal:   { label: 'Normal',   cls: 'text-slate-500', rang: 2 },
};
const TYP: Record<string, { label: string; icon: string }> = {
  fehler: { label: 'Fehler', icon: '🐞' }, wunsch: { label: 'Wunsch', icon: '💡' }, frage: { label: 'Frage', icon: '❓' },
};
type Ansicht = 'aktiv' | TicketStatus | 'alle';
const WOCHE_MS = 7 * 24 * 3600 * 1000;
const utc = (v?: string | null) => (v ? Date.parse(/[zZ]$/.test(v) ? v : v.replace(' ', 'T') + 'Z') : NaN);

function StatusPill({ s }: { s: string }) {
  const i = statusInfo(s);
  return <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${i.cls}`}>{i.label}</span>;
}
function PrioPill({ p }: { p: string }) {
  const i = PRIO[p] ?? PRIO.normal!;
  if (p === 'normal') return <span className="text-xs text-slate-400">Normal</span>;
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${i.cls}`}>{p === 'dringend' ? '▲▲ ' : '▲ '}{i.label}</span>;
}
function Initialen({ name, kurzel }: { name?: string | null; kurzel?: string | null }) {
  const k = (kurzel || (name ?? '').split(/\s+/).map(w => w[0]).join('').slice(0, 2) || '?').toUpperCase();
  return <span title={name ?? undefined} className="inline-flex w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-bold items-center justify-center shrink-0">{k.slice(0, 3)}</span>;
}

// ── Kennzahlen ──────────────────────────────────────────────────────────────
function Kachel({ label, n, akzent, aktiv, onClick, testid }: { label: string; n: number; akzent: string; aktiv: boolean; onClick: () => void; testid: string }) {
  return (
    <button data-testid={testid} onClick={onClick}
            className={`text-left bg-white rounded-xl border px-4 py-3 transition hover:border-slate-300 ${aktiv ? 'border-slate-800 ring-1 ring-slate-800' : 'border-slate-200'}`}>
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500"><span className={`w-2 h-2 rounded-full ${akzent}`} />{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-0.5 tabular-nums">{n}</div>
    </button>
  );
}

// ── Anhänge (im Panel) ──────────────────────────────────────────────────────
function Anhaenge({ t }: { t: Ticket }) {
  const dat = useApi(() => api.tickets.dateien(t.id), [t.id]);
  const [busy, setBusy] = useState(false);
  const upload = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { await api.tickets.dateiHochladen(t.id, f); dat.reload(); }
    catch (err) { alert('Upload fehlgeschlagen: ' + (err as Error).message); }
    setBusy(false);
    e.target.value = '';
  };
  const loeschen = async (name: string) => {
    if (!confirm(`Anhang „${name}" löschen?`)) return;
    setBusy(true);
    try { await api.tickets.dateiLoeschen(t.id, name); dat.reload(); }
    catch (err) { alert('Fehler: ' + (err as Error).message); }
    setBusy(false);
  };
  const dateien = dat.data?.data ?? [];
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {dateien.map(d => {
          const url = api.tickets.dateiUrl(t.id, d.name);
          const bild = (d.content_type ?? '').startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(d.name);
          return (
            <div key={d.name} className="relative group border border-slate-200 rounded-lg overflow-hidden bg-white">
              <a href={url} target="_blank" rel="noreferrer" title={d.name}>
                {bild ? <img src={url} alt={d.name} className="h-16 w-16 object-cover" />
                      : <div className="h-16 w-16 flex flex-col items-center justify-center text-slate-500 text-[10px] p-1"><span className="text-xl">📄</span><span className="truncate max-w-[56px]">{d.name}</span></div>}
              </a>
              <button onClick={() => loeschen(d.name)} disabled={busy} title="Löschen"
                      className="absolute top-0.5 right-0.5 bg-white/90 rounded text-slate-400 hover:text-red-600 text-xs w-4 h-4 leading-none opacity-0 group-hover:opacity-100">×</button>
            </div>
          );
        })}
        <label className="h-16 w-16 rounded-lg border border-dashed border-slate-300 text-slate-400 hover:text-slate-600 hover:border-slate-400 flex flex-col items-center justify-center text-[10px] cursor-pointer">
          <span className="text-lg leading-none">＋</span>{busy ? 'lädt …' : 'Datei'}
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} disabled={busy} />
        </label>
      </div>
    </div>
  );
}

// ── Detail-Panel ────────────────────────────────────────────────────────────
function Panel({ t, onClose, onChange }: { t: Ticket; onClose: () => void; onChange: () => void }) {
  const verlauf = useApi(() => api.tickets.verlauf(t.id), [t.id, t.updated_at, t.status]);
  const [busy, setBusy] = useState(false);
  const [antwort, setAntwort] = useState('');
  const [dialog, setDialog] = useState<{ status: 'rueckfrage' | 'abgelehnt'; text: string } | null>(null);
  useEffect(() => { setDialog(null); setAntwort(''); }, [t.id]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !dialog) onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [dialog, onClose]);

  const tun = async (f: () => Promise<unknown>) => {
    setBusy(true);
    try { await f(); onChange(); verlauf.reload(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  };
  const status = (s: Ticket['status'], extra?: { grund?: string; nachricht?: string }) =>
    tun(async () => { await api.tickets.update(t.id, { status: s, ...extra }); setDialog(null); });
  const senden = () => antwort.trim() && tun(async () => { await api.tickets.kommentar(t.id, antwort.trim()); setAntwort(''); });
  const loeschen = () => { if (confirm(`Ticket #T-${t.id} „${t.titel}" löschen?`)) void tun(async () => { await api.tickets.remove(t.id); onClose(); }); };
  const ty = TYP[t.typ] ?? TYP.fehler!;
  const zu = istAbgeschlossen(t.status);

  return (
    <aside data-testid="ticket-panel" aria-label={`Ticket #T-${t.id}`}
           className="fixed inset-0 z-40 bg-white flex flex-col lg:static lg:z-auto lg:w-[460px] lg:shrink-0 lg:border-l lg:border-slate-200 lg:sticky lg:top-0 lg:h-screen">
      {/* Kopf */}
      <div className="px-5 pt-4 pb-3 border-b border-slate-200">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="font-mono font-semibold text-slate-700">#T-{t.id}</span>
          <span>·</span><span>{ty.icon} {ty.label}</span>
          {t.bereich && <><span>·</span><span>{t.bereich}</span></>}
          <button data-testid="panel-zu" onClick={onClose} aria-label="Schließen" className="ml-auto text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
        </div>
        <h2 className="text-lg font-semibold text-slate-900 mt-1 leading-snug">{t.titel}</h2>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <StatusPill s={t.status} />
          <PrioPill p={t.prio} />
          <span className="text-xs text-slate-500">von <b className="font-medium text-slate-700">{t.gemeldet_von || t.gemeldet_kurzel || '?'}</b> · {fmtDatumZeit(t.created_at)}</span>
        </div>
      </div>

      {/* Aktionen */}
      <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/60 space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-xs text-slate-500 w-20">Bearbeiter</span>
          {t.zugewiesen_an
            ? <><Initialen name={t.zugewiesen_name} kurzel={t.zugewiesen_kurzel} /><span className="text-slate-800">{t.zugewiesen_name || t.zugewiesen_kurzel}</span>
                {!zu && <button disabled={busy} onClick={() => void tun(() => api.tickets.update(t.id, { zugewiesen_an: null }))} className="ml-auto text-xs text-slate-500 hover:text-slate-800">Freigeben</button>}</>
            : <><span className="text-slate-400">— niemand</span>
                {!zu && <button data-testid="panel-uebernehmen" disabled={busy} onClick={() => void tun(() => api.tickets.uebernehmen(t.id))} className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-700">Übernehmen</button>}</>}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-xs text-slate-500 w-20">Priorität</span>
          <select value={t.prio} disabled={busy} onChange={e => void tun(() => api.tickets.update(t.id, { prio: e.target.value as Ticket['prio'] }))}
                  className="text-sm border border-slate-200 rounded-md px-2 py-1 bg-white">
            <option value="normal">Normal</option><option value="wichtig">Wichtig</option><option value="dringend">Dringend</option>
          </select>
        </div>
        <div className="flex gap-1.5 flex-wrap pt-1">
          {zu ? (
            <button disabled={busy} onClick={() => void status('offen')} className="text-xs font-medium px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50">↩ Wieder öffnen</button>
          ) : <>
            {t.status !== 'in_arbeit' && <button disabled={busy} onClick={() => void status('in_arbeit')} className="text-xs font-medium px-3 py-1.5 rounded-md border border-sky-200 bg-white text-sky-700 hover:bg-sky-50">In Arbeit</button>}
            {t.status !== 'rueckfrage' && <button data-testid="btn-rueckfrage" disabled={busy} onClick={() => setDialog({ status: 'rueckfrage', text: '' })} className="text-xs font-medium px-3 py-1.5 rounded-md border border-amber-300 bg-white text-amber-800 hover:bg-amber-50">Rückfrage</button>}
            <button data-testid="btn-erledigt" disabled={busy} onClick={() => void status('erledigt')} className="text-xs font-semibold px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700">✓ Erledigt</button>
            <button data-testid="btn-ablehnen" disabled={busy} onClick={() => setDialog({ status: 'abgelehnt', text: '' })} className="text-xs font-medium px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50">Ablehnen</button>
          </>}
        </div>
        {dialog && (
          <div data-testid="status-dialog" className={`rounded-lg border p-3 ${dialog.status === 'rueckfrage' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
            <div className="text-sm font-semibold text-slate-800">{dialog.status === 'rueckfrage' ? 'Rückfrage an den Melder' : 'Ticket ablehnen'}</div>
            <div className="text-xs text-slate-500 mb-2">{dialog.status === 'rueckfrage' ? `${t.gemeldet_von || 'Der Melder'} sieht die Frage in „Meine Tickets“ und bekommt einen Hinweis.` : 'Der Grund wird dem Melder angezeigt (Pflicht).'}</div>
            <textarea data-testid="status-dialog-text" rows={2} autoFocus value={dialog.text} onChange={e => setDialog({ ...dialog, text: e.target.value })}
                      placeholder={dialog.status === 'rueckfrage' ? 'Deine Frage …' : 'Grund …'} className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm" />
            <div className="flex gap-2 mt-2 justify-end">
              <button onClick={() => setDialog(null)} className="text-xs px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 bg-white">Abbrechen</button>
              <button data-testid="status-dialog-ok" disabled={busy || !dialog.text.trim()}
                      onClick={() => void status(dialog.status, dialog.status === 'abgelehnt' ? { grund: dialog.text.trim() } : { nachricht: dialog.text.trim() })}
                      className="text-xs font-semibold px-3 py-1.5 rounded-md bg-slate-800 text-white disabled:opacity-40">{dialog.status === 'rueckfrage' ? 'Frage senden' : 'Ablehnen'}</button>
            </div>
          </div>
        )}
      </div>

      {/* Inhalt */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {t.beschreibung && (
          <section>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Beschreibung</div>
            <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{t.beschreibung}</div>
          </section>
        )}
        <section>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Anhänge</div>
          <Anhaenge t={t} />
        </section>
        <section>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Verlauf</div>
          {verlauf.loading && !verlauf.data && <div className="text-xs text-slate-400">Lädt …</div>}
          {verlauf.data && <Zeitleiste eintraege={verlauf.data.data} />}
        </section>
        <button onClick={loeschen} disabled={busy} className="text-xs text-slate-400 hover:text-red-600">Ticket löschen</button>
      </div>

      {/* Antworten */}
      <div className="px-5 py-3 border-t border-slate-200 flex gap-2">
        <textarea data-testid="panel-antwort" rows={1} value={antwort} onChange={e => setAntwort(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void senden(); } }}
                  placeholder={`Antwort an ${t.gemeldet_von?.split(/\s+/)[0] || 'Melder'} … (Enter = senden)`}
                  className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm resize-none" />
        <button data-testid="panel-senden" disabled={busy || !antwort.trim()} onClick={() => void senden()}
                className="text-sm font-semibold px-3 rounded-md bg-slate-800 text-white disabled:opacity-40">Senden</button>
      </div>
    </aside>
  );
}

// ── Seite ───────────────────────────────────────────────────────────────────
export function Tickets() {
  const list = useApi(() => api.tickets.list('alle', ''), []);
  const { daten: neuDaten, gesehen: neuGesehen, istNeu } = useTicketNeu();
  const [sp, setSp] = useSearchParams();
  const [ansicht, setAnsicht] = useState<Ansicht>('aktiv');
  const [suche, setSuche] = useState('');
  const [bereich, setBereich] = useState('');
  const [typ, setTyp] = useState('');
  const [prio, setPrio] = useState('');
  const [melder, setMelder] = useState('');
  const [bearb, setBearb] = useState('');
  const [auswahl, setAuswahl] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [filterAuf, setFilterAuf] = useState(false);   // nur mobil relevant
  const offenId = Number(sp.get('ticket')) || null;
  const oeffnen = (id: number | null) => {
    const n = new URLSearchParams(sp);
    if (id) n.set('ticket', String(id)); else n.delete('ticket');
    setSp(n, { replace: true });
    if (id && istNeu(id)) neuGesehen([id]);
  };

  // Neues Ticket trifft ein, während die Liste offen ist → nachladen
  const neuIds = neuDaten.tickets.map(t => t.id).join(',');
  useEffect(() => { if (neuIds) list.reload(); }, [neuIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const alle = list.data?.data ?? [];
  const jetzt = Date.now();
  const zahl = useMemo(() => ({
    offen: alle.filter(t => t.status === 'offen').length,
    in_arbeit: alle.filter(t => t.status === 'in_arbeit').length,
    rueckfrage: alle.filter(t => t.status === 'rueckfrage').length,
    erledigt: alle.filter(t => t.status === 'erledigt' && jetzt - utc(t.erledigt_at) < WOCHE_MS).length,
  }), [alle]); // eslint-disable-line react-hooks/exhaustive-deps
  const melderListe = useMemo(() => [...new Set(alle.map(t => t.gemeldet_von).filter(Boolean) as string[])].sort(), [alle]);
  const bearbListe = useMemo(() => [...new Set(alle.map(t => t.zugewiesen_name).filter(Boolean) as string[])].sort(), [alle]);

  const rows = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return alle.filter(t =>
      (ansicht === 'alle' ? true : ansicht === 'aktiv' ? !istAbgeschlossen(t.status) : t.status === ansicht)
      && (!bereich || t.bereich === bereich) && (!typ || t.typ === typ) && (!prio || t.prio === prio)
      && (!melder || t.gemeldet_von === melder)
      && (!bearb || (bearb === '—' ? !t.zugewiesen_an : t.zugewiesen_name === bearb))
      && (!q || `#t-${t.id} ${t.titel} ${t.beschreibung ?? ''} ${t.gemeldet_von ?? ''}`.toLowerCase().includes(q)));
  }, [alle, ansicht, suche, bereich, typ, prio, melder, bearb]);
  const filterAktiv = !!(suche || bereich || typ || prio || melder || bearb);
  const offen = offenId ? alle.find(t => t.id === offenId) ?? null : null;

  // Mehrfachauswahl
  const sichtbarIds = rows.map(t => t.id);
  const gewaehlt = sichtbarIds.filter(id => auswahl.has(id));
  const alleGewaehlt = sichtbarIds.length > 0 && gewaehlt.length === sichtbarIds.length;
  const umschalten = (id: number) => setAuswahl(a => { const n = new Set(a); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const sammel = async (art: 'uebernehmen' | 'in_arbeit' | 'erledigt' | 'gesehen') => {
    const ids = gewaehlt;
    if (ids.length === 0) return;
    if (art === 'gesehen') { neuGesehen(ids.filter(istNeu)); setAuswahl(new Set()); return; }
    if (art === 'erledigt' && !confirm(`${ids.length} Ticket(s) als erledigt markieren?`)) return;
    setBusy(true);
    const fehler: number[] = [];
    for (const id of ids) {
      try {
        if (art === 'uebernehmen') await api.tickets.uebernehmen(id);
        else await api.tickets.update(id, { status: art });
      } catch { fehler.push(id); }
    }
    setBusy(false); setAuswahl(new Set()); list.reload();
    if (fehler.length) alert(`Nicht geklappt bei: ${fehler.map(i => '#T-' + i).join(', ')}`);
  };

  if (list.data && list.data.bearbeiter === false) return <Navigate to="/meine-tickets" replace />;

  const sel = 'border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white text-slate-700';
  // Panel offen → schmalere Liste: nur Ticket, Status, Bearbeiter (Melder/Prio/Zeit wandern in die Unterzeile)
  const kompakt = !!offen;
  const raster = kompakt ? 'md:grid-cols-[28px_minmax(0,1fr)_100px_44px]' : 'md:grid-cols-[28px_minmax(0,1fr)_130px_100px_90px_140px_90px]';
  const breit = kompakt ? 'hidden' : 'hidden md:block';
  return (
    <div className="flex min-h-full">
      <div className="flex-1 min-w-0 p-4 md:p-8">
        <header className="mb-5 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Ticket-Master</div>
            <h1 className="text-2xl font-bold text-slate-900">Tickets</h1>
          </div>
          {neuDaten.tickets.length > 0 && (
            <button data-testid="ticket-alle-gesehen" onClick={() => neuGesehen(neuDaten.tickets.map(t => t.id))}
                    className="text-xs bg-white text-red-700 border border-red-200 rounded-md px-3 py-1.5 font-semibold hover:bg-red-50">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1.5" />{neuDaten.tickets.length} neu — alle gesehen
            </button>
          )}
        </header>

        {/* Kennzahlen */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <Kachel testid="kachel-offen" label="Offen" n={zahl.offen} akzent="bg-rose-500" aktiv={ansicht === 'offen'} onClick={() => setAnsicht(ansicht === 'offen' ? 'aktiv' : 'offen')} />
          <Kachel testid="kachel-in_arbeit" label="In Arbeit" n={zahl.in_arbeit} akzent="bg-sky-500" aktiv={ansicht === 'in_arbeit'} onClick={() => setAnsicht(ansicht === 'in_arbeit' ? 'aktiv' : 'in_arbeit')} />
          <Kachel testid="kachel-rueckfrage" label="Rückfrage" n={zahl.rueckfrage} akzent="bg-amber-400" aktiv={ansicht === 'rueckfrage'} onClick={() => setAnsicht(ansicht === 'rueckfrage' ? 'aktiv' : 'rueckfrage')} />
          <Kachel testid="kachel-erledigt" label="Erledigt (7 Tage)" n={zahl.erledigt} akzent="bg-emerald-500" aktiv={ansicht === 'erledigt'} onClick={() => setAnsicht(ansicht === 'erledigt' ? 'aktiv' : 'erledigt')} />
 <div className="col-span-2 md:col-span-1 grid"><Kachel testid="kachel-alle" label="Alle Tickets" n={alle.length} akzent="bg-slate-400" aktiv={ansicht === 'alle'} onClick={() => setAnsicht(ansicht === 'alle' ? 'aktiv' : 'alle')} /></div>
        </div>

        {/* Suche + Filter */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" aria-hidden>⌕</span>
            <input data-testid="ticket-suche" value={suche} onChange={e => setSuche(e.target.value)} placeholder="Suchen: Titel, Text, #T-Nr, Melder"
                   className="w-full border border-slate-200 rounded-md pl-7 pr-3 py-1.5 text-sm bg-white" />
          </div>
          <button onClick={() => setFilterAuf(f => !f)} className="md:hidden text-sm border border-slate-200 rounded-md px-3 py-1.5 bg-white text-slate-700">
            Filter{filterAktiv ? ' •' : ''} {filterAuf ? '▴' : '▾'}
          </button>
          <div className={`${filterAuf ? 'flex' : 'hidden'} md:flex items-center gap-2 flex-wrap w-full md:w-auto`}>
          <select aria-label="Bereich" value={bereich} onChange={e => setBereich(e.target.value)} className={sel}>
            <option value="">Bereich: alle</option>
            {TICKET_BEREICH_GRUPPEN.map(g => <optgroup key={g.gruppe} label={g.gruppe}>{g.bereiche.map(b => <option key={b} value={b}>{b}</option>)}</optgroup>)}
          </select>
          <select aria-label="Typ" data-testid="filter-typ" value={typ} onChange={e => setTyp(e.target.value)} className={sel}>
            <option value="">Typ: alle</option>{Object.entries(TYP).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
          </select>
          <select aria-label="Priorität" value={prio} onChange={e => setPrio(e.target.value)} className={sel}>
            <option value="">Prio: alle</option><option value="dringend">Dringend</option><option value="wichtig">Wichtig</option><option value="normal">Normal</option>
          </select>
          <select aria-label="Melder" value={melder} onChange={e => setMelder(e.target.value)} className={sel}>
            <option value="">Melder: alle</option>{melderListe.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select aria-label="Bearbeiter" value={bearb} onChange={e => setBearb(e.target.value)} className={sel}>
            <option value="">Bearbeiter: alle</option><option value="—">— nicht zugewiesen</option>{bearbListe.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          </div>
          {filterAktiv && <button onClick={() => { setSuche(''); setBereich(''); setTyp(''); setPrio(''); setMelder(''); setBearb(''); }} className="text-xs text-slate-500 hover:text-slate-800 px-1">Filter zurücksetzen</button>}
        </div>

        {/* Sammelleiste */}
        {gewaehlt.length > 0 && (
          <div data-testid="sammelleiste" className="sticky top-0 z-10 mb-2 flex items-center gap-2 flex-wrap bg-slate-900 text-white rounded-lg px-3 py-2 text-sm shadow">
            <span className="font-semibold">{gewaehlt.length} ausgewählt</span>
            <span className="text-slate-500">|</span>
            <button data-testid="sammel-uebernehmen" disabled={busy} onClick={() => void sammel('uebernehmen')} className="px-2 py-0.5 rounded hover:bg-white/10">Übernehmen</button>
            <button disabled={busy} onClick={() => void sammel('in_arbeit')} className="px-2 py-0.5 rounded hover:bg-white/10">In Arbeit</button>
            <button data-testid="sammel-erledigt" disabled={busy} onClick={() => void sammel('erledigt')} className="px-2 py-0.5 rounded hover:bg-white/10">✓ Erledigt</button>
            <button disabled={busy} onClick={() => void sammel('gesehen')} className="px-2 py-0.5 rounded hover:bg-white/10">Als gesehen</button>
            <button onClick={() => setAuswahl(new Set())} className="ml-auto text-slate-400 hover:text-white text-xs">Auswahl aufheben</button>
          </div>
        )}

        {/* Liste */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className={`hidden md:grid ${raster} gap-3 items-center px-4 py-2 border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500`}>
            <input type="checkbox" aria-label="Alle auswählen" checked={alleGewaehlt} onChange={() => setAuswahl(alleGewaehlt ? new Set() : new Set(sichtbarIds))} />
            <span>Ticket</span>{!kompakt && <span>Melder</span>}<span>Status</span>{!kompakt && <span>Prio</span>}<span>{kompakt ? '' : 'Bearbeiter'}</span>{!kompakt && <span className="text-right">Aktivität</span>}
          </div>
          {list.loading && !list.data && <div className="p-6 text-sm text-slate-500">Lädt …</div>}
          {list.data && rows.length === 0 && (
            <div className="p-12 text-center text-sm text-slate-400">{filterAktiv || ansicht !== 'aktiv' ? 'Keine Tickets für diese Auswahl.' : 'Keine offenen Tickets. 🎉'}</div>
          )}
          {rows.map(t => {
            const neu = istNeu(t.id);
            const ty = TYP[t.typ] ?? TYP.fehler!;
            const gewaehltZeile = auswahl.has(t.id);
            return (
              <div key={t.id} data-testid="ticket-karte" data-status={t.status} onClick={() => oeffnen(t.id)}
                   className={`grid grid-cols-[28px_minmax(0,1fr)] ${raster} gap-x-3 gap-y-1 items-center px-4 py-3 border-b border-slate-100 last:border-b-0 cursor-pointer
                     ${offenId === t.id ? 'bg-sky-50' : gewaehltZeile ? 'bg-slate-50' : 'hover:bg-slate-50'} ${istAbgeschlossen(t.status) ? 'text-slate-500' : ''}`}>
                <input type="checkbox" aria-label={`#T-${t.id} auswählen`} checked={gewaehltZeile}
                       onClick={e => e.stopPropagation()} onChange={() => umschalten(t.id)} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {neu && <span data-testid="ticket-neu-etikett" title="Neu" className="w-2 h-2 rounded-full bg-red-500 shrink-0" />}
                    <span className="font-mono text-[11px] text-slate-400 shrink-0">#T-{t.id}</span>
                    <span className="shrink-0" title={ty.label}>{ty.icon}</span>
                    <span className={`truncate ${neu ? 'font-semibold text-slate-900' : 'font-medium text-slate-800'}`}>{t.titel}</span>
                    {!!t.komm_anzahl && <span className="text-[11px] text-slate-400 shrink-0">💬 {t.komm_anzahl}</span>}
                  </div>
                  <div className="text-xs text-slate-400 truncate mt-0.5">
                    {t.bereich ?? 'ohne Bereich'}{t.beschreibung ? ` · ${t.beschreibung.replace(/\s+/g, ' ').slice(0, 110)}` : ''}
                  </div>
                  {/* Mobil: Kurzinfo statt Spalten */}
                  <div className={`${kompakt ? '' : 'md:hidden '}flex items-center gap-2 mt-1.5 flex-wrap text-xs text-slate-500`}>
                    <span className={kompakt ? 'md:hidden' : ''}><StatusPill s={t.status} /></span><PrioPill p={t.prio} /><span>{t.gemeldet_kurzel || t.gemeldet_von}</span><span>· {relativZeit(t.updated_at || t.created_at)}</span>
                  </div>
                </div>
                <div className={`${breit} text-sm text-slate-600 truncate`}>{t.gemeldet_von || t.gemeldet_kurzel || '?'}</div>
                <div className="hidden md:block"><StatusPill s={t.status} /></div>
                <div className={breit}><PrioPill p={t.prio} /></div>
                <div className="hidden md:flex items-center gap-1.5 min-w-0">
                  {t.zugewiesen_an ? <><Initialen name={t.zugewiesen_name} kurzel={t.zugewiesen_kurzel} />{!kompakt && <span className="text-sm text-slate-600 truncate">{t.zugewiesen_name}</span>}</>
                                   : <span className="text-xs text-slate-300">—</span>}
                </div>
                <div className={`${breit} text-xs text-slate-400 text-right whitespace-nowrap`} title={fmtDatumZeit(t.updated_at || t.created_at)}>{relativZeit(t.updated_at || t.created_at)}</div>
              </div>
            );
          })}
        </div>
        <div className="text-xs text-slate-400 mt-2">{rows.length} von {alle.length} Tickets · Klick auf eine Zeile öffnet die Details · Esc schließt</div>
      </div>

      {offen && <Panel t={offen} onClose={() => oeffnen(null)} onChange={() => list.reload()} />}
    </div>
  );
}

